import React from "react";
import { isEmpty } from "lodash";
import { checkValidURL } from "@/utils/formHelper";
import { QREditorContextProps, QRProps } from "@/contexts/QREditorContext/types";
import { QREditorContext } from "@/contexts/QREditorContext";
import { TimezonesService } from "@/services/TimezonesService";
import { TCalendarEvent } from "@/services/AddToCalendarService/types.ts";
import { PreviewLayout } from ".";
import { Icons } from "@/types/icons";
import { Typography } from "../Typography";
import { IconList } from "../IconList";
import { Link } from "../Link";
import { QREventType } from "@/types/qr";
import { LocationType } from "../PreviewLocationBlock/types";
import { getFullAddress } from "@/utils/qrEditorHelper";
import { PreviewHeadingBlock } from "../PreviewHeadingBlock";
import { PreviewEventDateBlock } from "../PreviewEventDateBlock";
import { PreviewLocationBlock } from "../PreviewLocationBlock";
import { PreviewPersonalInformationBlock } from "../PreviewPersonalInformationBlock";
import { LabeledDataType } from "../LabeledData/types";

export default function PreviewEvent(props: QRProps<"event">) {
  const t = props.previewTranslations;

  const qrEditorContextData = React.useContext<QREditorContextProps<"event">>(QREditorContext);
  const isInitPreview = props?.initPreview;

  const data = props?.data || qrEditorContextData?.qrData.data;

  const {
    eventDateTime,
    logoUrl,
    infoTitle,
    infoDescription,
    infoActionTitle,
    infoActionUrl,
    themeColor,
    facilities,
  } = data;

  const { addressData, organizerData } = getPreviewFormattedData(data, isInitPreview, t);

  const calendarEvent: TCalendarEvent = {
    name: infoTitle,
    details: infoDescription,
    location: addressData?.fullAddress,
    startsAt: TimezonesService.convertDateFromTimezoneToLocalTimezone(
      eventDateTime?.start?.date,
      eventDateTime?.allDay ? "0:00" : eventDateTime?.start?.time,
      eventDateTime?.timezone
    ),
    endsAt: TimezonesService.convertDateFromTimezoneToLocalTimezone(
      eventDateTime?.end?.date,
      eventDateTime?.allDay ? "23:59" : eventDateTime?.end?.time,
      eventDateTime?.timezone
    ),
  };

  return (
    <PreviewLayout
      page="event"
      dataQA="preview-event-container"
      backgroundColor={themeColor?.backgroundColor}
      includeBrowser
      initPreview={isInitPreview}>
      <PreviewHeadingBlock
        isInitPreview={isInitPreview}
        image={logoUrl}
        title={infoTitle}
        text={infoDescription}
        actionText={infoActionTitle}
        actionUrl={infoActionUrl}
        actionColor={themeColor?.buttonColor}
      />

      <div className="qr-content">
        <PreviewEventDateBlock
          title={isInitPreview ? "Date of the event" : t && t.eventPreviewTime}
          data={eventDateTime}
          labels={{
            start: isInitPreview ? "Start" : t && t.eventPreviewTimeStart,
            end: isInitPreview ? "End" : t && t.eventPreviewTimeEnd,
            schedule: isInitPreview ? "Schedule" : t && t.eventPreviewTimeSchedule,
            allDay: isInitPreview ? "All Day" : t && t.eventPreviewAllDay,
          }}
          calendarEvent={calendarEvent}
          themeColor={themeColor}
        />

        <PreviewLocationBlock
          title={isInitPreview ? "Location" : t && t.eventPreviewLocation}
          data={addressData}
        />

        <PreviewPersonalInformationBlock
          title={isInitPreview ? "Organizer information" : t && t.eventPreviewOrganizer}
          iconName={Icons.users}
          data={organizerData}
        />

        {!isEmpty(facilities) && (
          <div className="u-card">
            <Typography
              Tag="p"
              size="m"
              classname="u-semibold">
              {(t && t.eventPreviewFacilities) || "Facilities"}
            </Typography>
            <IconList
              compact
              items={facilities as unknown as string[]}
            />
          </div>
        )}
      </div>
    </PreviewLayout>
  );
}

const getPreviewFormattedData = (data: QREventType, isInitPreview: boolean, t: any) => {
  const {
    locationAddress,
    locationNumeration,
    locationPostalCode,
    locationCity,
    locationState,
    locationCountry,
    organizerName,
    organizerPhone,
    organizerEmail,
    organizerWebsite,
    organizerAbout,
  } = data;

  const addressData: LocationType = {
    address: locationAddress,
    numeration: locationNumeration,
    zipCode: locationPostalCode,
    city: locationCity,
    state: locationState,
    country: locationCountry,
    fullAddress: getFullAddress(
      locationAddress,
      locationNumeration,
      locationPostalCode,
      locationCity,
      locationState,
      locationCountry
    ),
  };

  const organizerData: LabeledDataType[] = [
    {
      label: isInitPreview ? "Name" : t && t.eventPreviewOrganizerName,
      value: organizerName,
    },
    {
      label: isInitPreview ? "Phone" : t && t.eventPreviewOrganizerPhone,
      value: organizerPhone,
    },
    {
      label: isInitPreview ? "Email" : t && t.eventPreviewOrganizerEmail,
      value: organizerEmail && (
        <Link
          externalLink
          className="business-link"
          href={`mailto:${organizerEmail}`}>
          {organizerEmail}
        </Link>
      ),
    },
    {
      label: isInitPreview ? "Website" : t && t.eventPreviewOrganizerWebsite,
      value: organizerWebsite && (
        <Link
          externalLink
          className="business-link"
          href={checkValidURL(organizerWebsite) || ""}>
          {organizerWebsite}
        </Link>
      ),
    },
    {
      label: isInitPreview ? "About" : t && t.eventPreviewOrganizerAbout,
      value: organizerAbout,
    },
  ];

  return {
    addressData,
    organizerData,
  };
};
